import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { AllRawData } from '../../raw-types.ts';

export const StageSchema = defineSchema({
  name: 'serverStages',
  fields: [
    ['id', 'number'],
    ['rewards', { kind: 'dict', dict: 'delivery' }],
    ['retrieveCost', { kind: 'array', of: { kind: 'dict', dict: 'delivery' } }],
    ['starThresholds', { kind: 'record', value: 'number' }],
    ['waves', { kind: 'array', of: 'stageWave' }],
    // 콘텐츠 row 에 지정된 FIXED 난이도 템플릿 ID. 서버가 submit 시 재도출에 사용.
    ['fixedDifficultyTemplateId', 'string?'],
    // NEW — 반드시 맨 끝(positional 호환). 스테이지 입장 요구 티켓 수량. undefined = 1.
    ['ticketCost', 'number?'],
  ],
  lookupKeys: ['id'],
});

export interface RawStageRow {
  id: number;
  stage_reward: unknown;
  retrieve_cost: unknown;
  star_thresholds: unknown;
  fixed_difficulty_template_id?: string | null;
  ticket_cost?: number | null;
}

export interface StageInput {
  id: number;
  rewards: Record<string, number> | null;
  retrieveCost: Array<Record<string, number>> | null;
  starThresholds: Record<string, number> | null;
  waves: unknown[];
  fixedDifficultyTemplateId?: string | null;
  ticketCost?: number;
}

function parseJson<T>(v: unknown): T | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as T;
    } catch {
      return null;
    }
  }
  return v as T;
}

export function stageFromRaw(
  row: RawStageRow,
  raw: Pick<AllRawData, 'stage_wave' | 'stage_wave_enemy'>
): StageInput {
  const stageWaves = (raw.stage_wave ?? [])
    .filter((w) => w.stage_id === row.id)
    .sort((a, b) => a.wave_number - b.wave_number);

  const lastWaveNumber = stageWaves.length > 0 ? stageWaves[stageWaves.length - 1].wave_number : -1;

  const waves = stageWaves.map((w) => {
    // Shop never opens on the final wave (no chance to spend before clearing).
    const isLastWave = w.wave_number === lastWaveNumber;
    const shopConfigId = !isLastWave && w.shop_config_id != null ? w.shop_config_id : null;

    const enemies = (raw.stage_wave_enemy ?? [])
      .filter((e) => e.stage_id === row.id && e.wave_number === w.wave_number)
      .map((e) => ({
        id: e.enemy_id,
        count: 1,
        spawnInterval: e.spawn_interval,
        spawnPattern: e.spawn_pattern ?? null,
        healthMultiplier: e.health_multiplier,
        speedMultiplier: e.speed_multiplier,
        atkMultiplier: e.atk_multiplier,
      }));

    return {
      waveNumber: w.wave_number,
      killGoal: w.kill_goal,
      timeLimitSeconds: w.time_limit_seconds,
      silverReward: w.silver_reward,
      shopConfigId,
      enemies,
    };
  });

  return {
    id: row.id,
    rewards: parseJson<Record<string, number>>(row.stage_reward),
    retrieveCost: parseJson<Array<Record<string, number>>>(row.retrieve_cost),
    starThresholds: parseJson<Record<string, number>>(row.star_thresholds),
    waves,
    ...(row.fixed_difficulty_template_id != null
      ? { fixedDifficultyTemplateId: row.fixed_difficulty_template_id }
      : {}),
    ...(row.ticket_cost != null ? { ticketCost: row.ticket_cost } : {}),
  };
}

registerCategorySchema('serverStages', StageSchema);
